import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import {
  FaInfoCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaReceipt,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { TbArrowsExchange } from 'react-icons/tb';
import {
  FaUserFriends,
  FaSignInAlt,
  FaSignOutAlt,
  FaClock,
  FaSearchDollar,
  FaCalendarCheck,
  FaShieldAlt,
  FaQuestionCircle,
  FaEnvelope,
  FaPhoneAlt,
} from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Identity = () => {
  const location = useLocation();
  const { bookingDetails: initialBookingDetails } = location.state || {};

  // Get booking details from localStorage
  const storedBookingDetails = localStorage.getItem('bookingDetails');
  const parsedBookingDetails = storedBookingDetails ? JSON.parse(storedBookingDetails) : null;

  // Extract booking data from localStorage
  const orderId = parsedBookingDetails?.order_id || '';
  const primaryGuest = parsedBookingDetails?.primary_guest || '';
  const primaryBooking =
    parsedBookingDetails?.primaryBooking || parsedBookingDetails?.bookings?.[0];

  // Get booking ID from the primary booking
  const bookingId = primaryBooking?.booking_id || '';

  // Get guest information
  const guestName = primaryBooking?.guest_name || '';
  const guestEmail = primaryBooking?.guest_email || '';
  const guestPhone = primaryBooking?.guest_phone || '';
  const numberOfGuests = primaryBooking?.number_of_guests || 1;

  // Get current booking dates
  const originalCheckin = primaryBooking?.checkin_date || '';
  const originalCheckout = primaryBooking?.checkout_date || '';

  // Get room type information
  const roomTypeName = primaryBooking?.room_type?.name || '';

  const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache') || '{}');
  const hotelEmail = hotelPropertyCache?.data?.email || 'support@hotel.com';
  const phone = hotelPropertyCache?.data?.phone || '+1 234-567-890';

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [showPreviousReservationPopup, setShowPreviousReservationPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previousReservationDetails, setPreviousReservationDetails] = useState(null);
  const [loadingPreviousReservation, setLoadingPreviousReservation] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showTaxTooltip, setShowTaxTooltip] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();

  // Initialize dates from localStorage
  useEffect(() => {
    if (originalCheckin) {
      const checkinDate = new Date(originalCheckin);
      setStartDate(checkinDate);
    }
    if (originalCheckout) {
      const checkoutDate = new Date(originalCheckout);
      setEndDate(checkoutDate);
    }
  }, [originalCheckin, originalCheckout]);

  // Get current date (without time component)
  const getCurrentDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  // Get max allowed date (1 year from now)
  const getMaxAllowedDate = () => {
    const now = new Date();
    return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
  };

  const [minDate] = useState(getCurrentDate());
  const [maxDate] = useState(getMaxAllowedDate());

  // Format date for input[type="date"] (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateToDisplay = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculateTotalNights = (checkinDate, checkoutDate) => {
    if (!checkinDate || !checkoutDate) return 0;

    const checkin = new Date(checkinDate);
    const checkout = new Date(checkoutDate);
    const timeDiff = checkout - checkin;
    const nights = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return nights > 0 ? nights : 0;
  };

  const handleStartDateChange = (e) => {
    const newDate = new Date(e.target.value);
    setStartDate(newDate);
    // If new start date is after current end date, update end date too
    if (endDate && newDate > endDate) {
      setEndDate(newDate);
    }
    // Clear validation message when user changes date
    if (validationError) {
      setValidationError('');
      setShowValidationMessage(false);
    }
  };

  const handleEndDateChange = (e) => {
    const newDate = new Date(e.target.value);
    setEndDate(newDate);
    // Clear validation message when user changes date
    if (validationError) {
      setValidationError('');
      setShowValidationMessage(false);
    }
  };

  const validateDates = () => {
    const errors = [];

    if (!endDate || !startDate) {
      errors.push('Please select both check-in and check-out dates');
      return errors;
    }

    // Check if dates are the same as original booking
    const originalCheckinDate = originalCheckin ? new Date(originalCheckin) : null;
    const originalCheckoutDate = originalCheckout ? new Date(originalCheckout) : null;

    if (originalCheckinDate && originalCheckoutDate) {
      const isCheckinSame = startDate.getTime() === originalCheckinDate.getTime();
      const isCheckoutSame = endDate.getTime() === originalCheckoutDate.getTime();

      if (isCheckinSame && isCheckoutSame) {
        errors.push(
          "You haven't made any changes to your booking dates. Please select new dates to reschedule your stay."
        );
      }
    }

    if (endDate < startDate) {
      errors.push('Check-out date cannot be earlier than check-in date');
    }

    // Check if dates are in the past
    const today = getCurrentDate();
    if (endDate < today) {
      errors.push('Cannot select check-out date in the past');
    }

    // Check if dates are beyond max allowed date
    if (startDate > maxDate || endDate > maxDate) {
      errors.push(`Dates cannot be more than 1 year in advance`);
    }

    return errors;
  };

  // Call preview API to see updated price
  const handleUpdateDate = async () => {
    const errors = validateDates();

    if (errors.length > 0) {
      setValidationError(errors[0]);
      setShowValidationMessage(true);
      return;
    }

    const new_checkin_date = formatDateForInput(startDate);
    const new_checkout_date = formatDateForInput(endDate);

    setLoadingPreview(true);
    try {
      // Call the preview API
      const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${bookingId}/preview`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_checkin_date: new_checkin_date,
          new_checkout_date: new_checkout_date,
          new_room_type_name: roomTypeName, // Use current room type
        }),
      });

      const data = await response.json();
      if (response.ok && data.status === 'success') {
        setPreviewData(data.data);
        setShowPopup(true);
      } else {
        console.error('Failed to fetch preview details:', data);
        setPopupMessage(data.message || data.detail || 'Failed to fetch preview details.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error occurred while fetching preview details:', error);
      setPopupMessage('An error occurred while fetching preview details.');
      setShowErrorPopup(true);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Call modify API to confirm the changes - FIXED: Using query parameters
  const handleContinue = async () => {
    const new_checkin_date = formatDateForInput(startDate);
    const new_checkout_date = formatDateForInput(endDate);

    try {
      // Build URL with query parameters - FIXED: Using URLSearchParams
      const params = new URLSearchParams({
        new_room_type_name: roomTypeName,
        new_checkin_date: new_checkin_date,
        new_checkout_date: new_checkout_date,
      });

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/modify/${bookingId}/modify?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          // Send empty body as the API expects
          body: JSON.stringify({}),
        }
      );

      const data = await response.json();

      // FIX: Check for status: "success" in the response
      if (response.ok && data.status === 'success') {
        toast.success('Reservation modified successfully!', {
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
        });

        // Update localStorage with new dates
        if (parsedBookingDetails) {
          const updatedBookingDetails = {
            ...parsedBookingDetails,
            bookings: parsedBookingDetails.bookings?.map((booking) => {
              if (booking.booking_id === bookingId) {
                return {
                  ...booking,
                  checkin_date: new_checkin_date,
                  checkout_date: new_checkout_date,
                  length_of_stay: calculateTotalNights(new_checkin_date, new_checkout_date),
                };
              }
              return booking;
            }),
            primaryBooking: {
              ...parsedBookingDetails.primaryBooking,
              checkin_date: new_checkin_date,
              checkout_date: new_checkout_date,
              length_of_stay: calculateTotalNights(new_checkin_date, new_checkout_date),
            },
          };

          localStorage.setItem('bookingDetails', JSON.stringify(updatedBookingDetails));
        }

        setTimeout(() => {
          navigate('/reservation/options/booking/welcome');
        }, 3000);
        setShowPopup(false);
      } else {
        console.error('Failed to modify reservation:', data);
        const errorMessage = data.message || data.detail || 'Failed to modify the reservation.';
        setPopupMessage(errorMessage);
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error occurred while modifying reservation:', error);
      setPopupMessage('An error occurred while modifying reservation.');
      setShowErrorPopup(true);
    }
  };

  const fetchPreviousReservationDetails = async () => {
    setLoadingPreviousReservation(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${bookingId}/options`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        const transformedData = {
          booking: {
            order_id: orderId,
            room_type: data.data.current_room_type,
            checkin_date: data.data.booking_actual_dates.checkin,
            checkout_date: data.data.booking_actual_dates.checkout,
            number_of_guests: numberOfGuests,
            length_of_stay: calculateTotalNights(
              data.data.booking_actual_dates.checkin,
              data.data.booking_actual_dates.checkout
            ),
          },
          payment_summary: {
            total_amount: data.data.current_room_total,
          },
        };

        setPreviousReservationDetails(transformedData);
        setShowPreviousReservationPopup(true);
      } else {
        console.error('Failed to fetch reservation details:', data);
        setPopupMessage(data.message || data.detail || 'Failed to fetch reservation details.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error occurred while fetching reservation details:', error);
      setPopupMessage('An error occurred while fetching reservation details.');
      setShowErrorPopup(true);
    } finally {
      setLoadingPreviousReservation(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
  };

  const closePreviousReservationPopup = () => {
    setShowPreviousReservationPopup(false);
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numAmount)) return '₹0.00';

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  // Calculate extended days
  const calculateExtendedDays = () => {
    if (!previewData?.room_preview) return 0;
    return (
      previewData.room_preview.total_nights - (previewData.room_preview.breakdown_old?.length || 0)
    );
  };

  const extendedDays = calculateExtendedDays();

  // Filter out duplicate tax entries from price details
  const getFilteredPriceDetails = () => {
    if (!previewData?.room_preview?.price_details) return {};

    const priceDetails = { ...previewData.room_preview.price_details };

    // Remove the duplicate fixed_tax_amount if it exists
    if ('fixed_tax_amount' in priceDetails) {
      delete priceDetails.fixed_tax_amount;
    }

    return priceDetails;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col items-center justify-start py-8 px-4 relative">
      {/* Back Navigation */}
      <div className="w-full max-w-7xl mb-3">
        <Link
          to="/reservation/options/booking/welcome"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-all duration-200 font-medium group"
        >
          <div className=" group-hover:border-gray-300 group-hover:shadow-sm transition-all">
            <IoMdArrowRoundBack size={18} />
          </div>
          <span className="group-hover:translate-x-[-2px] transition-transform">
            Back to Dashboard
          </span>
        </Link>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-7xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 "></div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Reschedule Your Stay
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Adjust your reservation dates to better suit your travel plans. All changes are subject
            to availability.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Combined Card */}
          <div className="lg:col-span-2">
            {/* Combined Reservation & Date Selection Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Card Header */}
              <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gray-300 border border-gray-200">
                    <FaCalendarAlt className="text-black text-xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Reschedule Your Booking</h2>
                    <p className="text-gray-600 text-sm">
                      Update your reservation details and dates
                    </p>
                  </div>
                </div>
              </div>

              {/* Reservation Details Section */}
              <div className="p-7 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-6">Reservation Details</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Guest Name
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-900">{guestName}</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reservation Number
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-900 font-mono">{orderId}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Number
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-900">{guestPhone}</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Guests
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-900">
                          {numberOfGuests} {numberOfGuests === 1 ? 'Guest' : 'Guests'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Selection Section */}
              <div className="p-7">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Select New Dates</h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Check-in Date */}
                  <div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Check-in Date
                      </label>
                    </div>
                    <div className="relative">
                      <input
                        type="date"
                        value={formatDateForInput(startDate)}
                        onChange={handleStartDateChange}
                        min={formatDateForInput(minDate)}
                        max={formatDateForInput(maxDate)}
                        className="w-full p-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:border-gray-600 focus:ring-1 focus:ring-gray-600"
                      />
                    </div>
                  </div>

                  {/* Check-out Date */}
                  <div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Check-out Date
                      </label>
                    </div>
                    <input
                      type="date"
                      value={formatDateForInput(endDate)}
                      onChange={handleEndDateChange}
                      min={formatDateForInput(startDate || minDate)}
                      max={formatDateForInput(maxDate)}
                      className="w-full p-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:border-gray-600 focus:ring-1 focus:ring-gray-600"
                    />
                  </div>
                </div>

                {/* Validation Message */}
                {showValidationMessage && validationError && (
                  <div className="mb-6">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start gap-3">
                        <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-700 mb-1">Please adjust your dates</p>
                          <p className="text-red-600 text-sm">{validationError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stay Duration Comparison */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-8">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Current Stay</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {calculateTotalNights(originalCheckin, originalCheckout)} nights
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">New Stay</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {calculateTotalNights(
                          formatDateForInput(startDate),
                          formatDateForInput(endDate)
                        )}{' '}
                        nights
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div>
                  <button
                    onClick={handleUpdateDate}
                    disabled={loadingPreview}
                    className="w-full py-3.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loadingPreview ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Calculating Changes...
                      </>
                    ) : (
                      'Preview Changes & Pricing'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Policy & Information */}
          <div className="space-y-8">
            {/* Policy Information */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gray-300 border border-gray-200">
                    <FaInfoCircle className="text-black" />
                  </div>
                  <h3 className="font-bold text-gray-900">Rescheduling Policy</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded-full bg-gray-300 mt-0.5">
                      <FaCalendarCheck className="text-black text-sm" />
                    </div>
                    <span className="text-gray-700">
                      Extension requests subject to availability and may incur additional charges
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded-full bg-gray-300 mt-0.5">
                      <FaExclamationTriangle className="text-black text-sm" />
                    </div>
                    <span className="text-gray-700">
                      Early checkout may be subject to fees per hotel policy
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded-full bg-gray-300 mt-0.5">
                      <FaShieldAlt className="text-black text-sm" />
                    </div>
                    <span className="text-gray-700">
                      Room rates are subject to change based on availability
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="p-1 rounded-full bg-gray-300 mt-0.5">
                      <FaClock className="text-black text-sm" />
                    </div>
                    <span className="text-gray-700">
                      Changes must be made at least 24 hours before original check-in
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Help Card */}
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg border border-blue-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-blue-100">
                  <FaQuestionCircle className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Need Help?</h3>
                  <p className="text-gray-600 text-sm">Our team is here to assist you</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="relative group">
                  <a
                    href={`mailto:${hotelEmail}`}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group-hover:bg-blue-50"
                  >
                    <FaEnvelope className="text-gray-500 group-hover:text-blue-600 transition-colors" />
                    <span className="text-gray-700 group-hover:text-gray-900">Email Support</span>
                  </a>
                  {/* Tooltip for email */}
                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
                    <span className="font-medium">{hotelEmail}</span>
                    <div className="absolute top-full left-6 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>

                <div className="relative group">
                  <a
                    href={`tel:${phone}`}
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group-hover:bg-blue-50"
                  >
                    <FaPhoneAlt className="text-gray-500 group-hover:text-blue-600 transition-colors" />
                    <span className="text-gray-700 group-hover:text-gray-900">Call Reception</span>
                  </a>
                  {/* Tooltip for phone */}
                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
                    <span className="font-medium">{phone}</span>
                    <div className="absolute top-full left-6 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Popup - Enhanced */}
      {showPopup && previewData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-300 animate-slideUp">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-gray-800 text-white p-7 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
                    <FaReceipt className="text-white text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Reschedule Preview</h2>
                    <p className="text-gray-300">Review your changes before confirming</p>
                  </div>
                </div>
                <button
                  onClick={closePopup}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-7 space-y-7">
              {/* Stay Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-gray-600" />
                    Original Stay
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Dates</p>
                      <p className="text-gray-900 font-medium">
                        {formatDateToDisplay(originalCheckin)} -{' '}
                        {formatDateToDisplay(originalCheckout)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Duration</p>
                      <p className="text-gray-900 font-bold text-xl">
                        {previewData.room_preview.breakdown_old?.length || 0} Nights
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total</p>
                      <p className="text-gray-900 font-bold text-xl">
                        {formatCurrency(previewData.room_preview.old_total)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200 relative">
                  <div className="absolute -top-3 -right-3 px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-bold rounded-full">
                    NEW
                  </div>
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-blue-600" />
                    New Stay
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Dates</p>
                      <p className="text-gray-900 font-medium">
                        {formatDateToDisplay(previewData.room_preview.new_checkin)} -{' '}
                        {formatDateToDisplay(previewData.room_preview.new_checkout)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Duration</p>
                      <p className="text-gray-900 font-bold text-xl">
                        {previewData.room_preview.total_nights} Nights
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total</p>
                      <p className="text-gray-900 font-bold text-xl">
                        {formatCurrency(previewData.room_preview.new_total)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Difference Highlight */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div>
                    <p className="text-gray-300">Price Difference</p>
                    <p className="text-3xl font-bold mt-1">
                      {formatCurrency(previewData.room_preview.price_difference)}
                    </p>
                    <p className="text-sm text-gray-400 mt-2">Including all taxes & fees</p>
                  </div>
                  <div className="text-center md:text-right">
                    <p className="text-gray-300">Change Type</p>
                    <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mt-1">
                      {previewData.room_preview.modification_type}
                    </span>
                    {extendedDays > 0 && (
                      <p className="text-green-300 text-sm mt-2">
                        +{extendedDays} additional nights
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-800">Detailed Price Breakdown</h3>
                  <button
                    onClick={() => setShowBreakdown(!showBreakdown)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {showBreakdown ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>

                {showBreakdown && previewData.room_preview.price_details && (
                  <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-200">
                    {Object.entries(getFilteredPriceDetails()).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-gray-900">{formatCurrency(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Final Summary */}
              <div className="bg-gradient-to-r from-green-50 to-white rounded-xl p-5 border border-green-200">
                <h3 className="font-bold text-gray-800 mb-4">Payment Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Current Balance Due</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(previewData.combined.current_due_before_preview)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Additional Charges</span>
                    <span className="font-medium text-green-600">
                      + {formatCurrency(previewData.combined.net_financial_impact)}
                    </span>
                  </div>
                  <div className="border-t border-gray-300 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">New Total Balance</span>
                      <span className="text-2xl font-bold text-gray-900">
                        {formatCurrency(previewData.combined.fixed_current_total)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closePopup}
                  className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContinue}
                  className="px-8 py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-xl hover:from-gray-800 hover:to-gray-700 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                >
                  Confirm Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-300 overflow-hidden animate-scaleIn">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>

            <div className="p-8 text-center">
              <div className="mb-6">
                <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-red-100 to-orange-100">
                  <BsFillExclamationTriangleFill className="text-red-600 text-4xl" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">Unable to Proceed</h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-red-700 font-medium whitespace-pre-line">{popupMessage}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-gray-700 mb-2">Need assistance?</p>
                <div className="flex flex-col gap-2">
                  <div className="relative group">
                    <a
                      href={`mailto:${hotelEmail}`}
                      className="text-gray-900 hover:text-gray-700 font-medium inline-flex items-center gap-2 justify-center w-full"
                    >
                      <FaEnvelope />
                      Email Support
                    </a>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
                      {hotelEmail}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>

                  <div className="relative group">
                    <a
                      href={`tel:${phone}`}
                      className="text-gray-900 hover:text-gray-700 font-medium inline-flex items-center gap-2 justify-center w-full"
                    >
                      <FaPhoneAlt />
                      Call {phone}
                    </a>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
                      {phone}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={closeErrorPopup}
                className="w-full py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-xl hover:from-gray-800 hover:to-gray-700 transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Identity;

// import React, { useState, useEffect } from "react";
// import { useLocation, Link } from "react-router-dom";
// import { IoMdArrowRoundBack } from "react-icons/io";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { useNavigate } from "react-router-dom";
// import { BsFillExclamationTriangleFill } from "react-icons/bs";
// import {
//   FaInfoCircle,
//   FaCalendarAlt,
//   FaMoneyBillWave,
//   FaReceipt,
//   FaExclamationTriangle,
// } from "react-icons/fa";
// import { TbArrowsExchange } from "react-icons/tb";
// // Add these imports at the top with your other imports
// import {
//   FaUserFriends,
//   FaSignInAlt,
//   FaSignOutAlt,
//   FaClock,
//   FaSearchDollar,
//   FaCalendarCheck,
//   FaShieldAlt,
//   FaQuestionCircle,
//   FaEnvelope,
//   FaPhoneAlt,
// } from "react-icons/fa";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Identity = () => {
//   const location = useLocation();
//   const { bookingDetails: initialBookingDetails } = location.state || {};

//   // Get booking details from localStorage
//   const storedBookingDetails = localStorage.getItem("bookingDetails");
//   const parsedBookingDetails = storedBookingDetails
//     ? JSON.parse(storedBookingDetails)
//     : null;

//   // Extract booking data from localStorage
//   const orderId = parsedBookingDetails?.order_id || "";
//   const primaryGuest = parsedBookingDetails?.primary_guest || "";
//   const primaryBooking =
//     parsedBookingDetails?.primaryBooking || parsedBookingDetails?.bookings?.[0];

//   // Get booking ID from the primary booking
//   const bookingId = primaryBooking?.booking_id || "";

//   // Get guest information
//   const guestName = primaryBooking?.guest_name || "";
//   const guestEmail = primaryBooking?.guest_email || "";
//   const guestPhone = primaryBooking?.guest_phone || "";
//   const numberOfGuests = primaryBooking?.number_of_guests || 1;

//   // Get current booking dates
//   const originalCheckin = primaryBooking?.checkin_date || "";
//   const originalCheckout = primaryBooking?.checkout_date || "";

//   // Get room type information
//   const roomTypeName = primaryBooking?.room_type?.name || "";

//   const hotelPropertyCache = JSON.parse(
//     localStorage.getItem("hotelPropertyCache") || "{}"
//   );
//   const hotelEmail = hotelPropertyCache?.data?.email || "support@hotel.com";
//   const phone = hotelPropertyCache?.data?.phone || "+1 234-567-890";

//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [showPopup, setShowPopup] = useState(false);
//   const [showErrorPopup, setShowErrorPopup] = useState(false);
//   const [showValidationMessage, setShowValidationMessage] = useState(false);
//   const [showPreviousReservationPopup, setShowPreviousReservationPopup] =
//     useState(false);
//   const [popupMessage, setPopupMessage] = useState("");
//   const [previewData, setPreviewData] = useState(null);
//   const [previousReservationDetails, setPreviousReservationDetails] =
//     useState(null);
//   const [loadingPreviousReservation, setLoadingPreviousReservation] =
//     useState(false);
//   const [loadingPreview, setLoadingPreview] = useState(false);
//   const [showTaxTooltip, setShowTaxTooltip] = useState(false);
//   const [showBreakdown, setShowBreakdown] = useState(false);
//   const [validationError, setValidationError] = useState("");
//   const navigate = useNavigate();

//   // Initialize dates from localStorage
//   useEffect(() => {
//     if (originalCheckin) {
//       const checkinDate = new Date(originalCheckin);
//       setStartDate(checkinDate);
//     }
//     if (originalCheckout) {
//       const checkoutDate = new Date(originalCheckout);
//       setEndDate(checkoutDate);
//     }
//   }, [originalCheckin, originalCheckout]);

//   // Get current date (without time component)
//   const getCurrentDate = () => {
//     const now = new Date();
//     return new Date(now.getFullYear(), now.getMonth(), now.getDate());
//   };

//   // Get max allowed date (1 year from now)
//   const getMaxAllowedDate = () => {
//     const now = new Date();
//     return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
//   };

//   const [minDate] = useState(getCurrentDate());
//   const [maxDate] = useState(getMaxAllowedDate());

//   // Format date for input[type="date"] (YYYY-MM-DD)
//   const formatDateForInput = (date) => {
//     if (!date) return "";
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const day = String(date.getDate()).padStart(2, "0");
//     return `${year}-${month}-${day}`;
//   };

//   const formatDateToDisplay = (dateString) => {
//     if (!dateString) return "";

//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-US", {
//       weekday: "short",
//       year: "numeric",
//       month: "short",
//       day: "numeric",
//     });
//   };

//   const calculateTotalNights = (checkinDate, checkoutDate) => {
//     if (!checkinDate || !checkoutDate) return 0;

//     const checkin = new Date(checkinDate);
//     const checkout = new Date(checkoutDate);
//     const timeDiff = checkout - checkin;
//     const nights = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

//     return nights > 0 ? nights : 0;
//   };

//   const handleStartDateChange = (e) => {
//     const newDate = new Date(e.target.value);
//     setStartDate(newDate);
//     // If new start date is after current end date, update end date too
//     if (endDate && newDate > endDate) {
//       setEndDate(newDate);
//     }
//     // Clear validation message when user changes date
//     if (validationError) {
//       setValidationError("");
//       setShowValidationMessage(false);
//     }
//   };

//   const handleEndDateChange = (e) => {
//     const newDate = new Date(e.target.value);
//     setEndDate(newDate);
//     // Clear validation message when user changes date
//     if (validationError) {
//       setValidationError("");
//       setShowValidationMessage(false);
//     }
//   };

//   const validateDates = () => {
//     const errors = [];

//     if (!endDate || !startDate) {
//       errors.push("Please select both check-in and check-out dates");
//       return errors;
//     }

//     // Check if dates are the same as original booking
//     const originalCheckinDate = originalCheckin
//       ? new Date(originalCheckin)
//       : null;
//     const originalCheckoutDate = originalCheckout
//       ? new Date(originalCheckout)
//       : null;

//     if (originalCheckinDate && originalCheckoutDate) {
//       const isCheckinSame =
//         startDate.getTime() === originalCheckinDate.getTime();
//       const isCheckoutSame =
//         endDate.getTime() === originalCheckoutDate.getTime();

//       if (isCheckinSame && isCheckoutSame) {
//         errors.push(
//           "You haven't made any changes to your booking dates. Please select new check-out date to reschedule your stay."
//         );
//       }
//     }

//     if (endDate < startDate) {
//       errors.push("Check-out date cannot be earlier than check-in date");
//     }

//     // Check if checkout is before original checkout (prior checkout)
//     if (originalCheckout) {
//       const originalCheckoutDate = new Date(originalCheckout);
//       if (endDate < originalCheckoutDate) {
//         errors.push(
//           "You cannot select a check-out date earlier than your original booking. Please select a later date to extend your stay."
//         );
//       }
//     }

//     // Check if dates are in the past
//     const today = getCurrentDate();
//     if (endDate < today) {
//       errors.push("Cannot select check-out date in the past");
//     }

//     // Check if dates are beyond max allowed date
//     if (startDate > maxDate || endDate > maxDate) {
//       errors.push(`Dates cannot be more than 1 year in advance`);
//     }

//     return errors;
//   };

//   // Call preview API to see updated price
//   const handleUpdateDate = async () => {
//     const errors = validateDates();

//     if (errors.length > 0) {
//       setValidationError(errors[0]);
//       setShowValidationMessage(true);
//       return;
//     }

//     const new_checkin_date = formatDateForInput(startDate);
//     const new_checkout_date = formatDateForInput(endDate);

//     setLoadingPreview(true);
//     try {
//       // Call the new preview API
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/modify/${bookingId}/preview`,
//         {
//           method: "POST",
//           headers: {
//             accept: "application/json",
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({
//             new_checkin_date: new_checkin_date,
//             new_checkout_date: new_checkout_date,
//             new_room_type_name: null, // Only changing dates, not room type
//             amenities: [],
//             room_services: [],
//             food_orders: [],
//           }),
//         }
//       );

//       const data = await response.json();
//       if (response.ok && data.status === "success") {
//         setPreviewData(data.data);
//         setShowPopup(true);
//       } else {
//         console.error("Failed to fetch preview details:", data);
//         setPopupMessage(
//           data.message || data.detail || "Failed to fetch preview details."
//         );
//         setShowErrorPopup(true);
//       }
//     } catch (error) {
//       console.error("Error occurred while fetching preview details:", error);
//       setPopupMessage("An error occurred while fetching preview details.");
//       setShowErrorPopup(true);
//     } finally {
//       setLoadingPreview(false);
//     }
//   };

//   // Call modify API to confirm the changes - FIXED SUCCESS CONDITION
//   const handleContinue = async () => {
//     const new_checkin_date = formatDateForInput(startDate);
//     const new_checkout_date = formatDateForInput(endDate);

//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/modify/${bookingId}/modify`,
//         {
//           method: "POST",
//           headers: {
//             accept: "application/json",
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({
//             new_room_type_name: null, // Only changing dates
//             new_checkin_date: new_checkin_date,
//             new_checkout_date: new_checkout_date,
//           }),
//         }
//       );

//       const data = await response.json();

//       // FIX: Check for both data.success AND data.status === "success"
//       if (response.ok && (data.success || data.status === "success")) {
//         toast.success("Reservation modified successfully!", {
//           position: "top-center",
//           autoClose: 4000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//           theme: "light",
//         });

//         // Update localStorage with new dates
//         if (parsedBookingDetails) {
//           const updatedBookingDetails = {
//             ...parsedBookingDetails,
//             bookings: parsedBookingDetails.bookings?.map((booking) => {
//               if (booking.booking_id === bookingId) {
//                 return {
//                   ...booking,
//                   checkin_date: new_checkin_date,
//                   checkout_date: new_checkout_date,
//                   length_of_stay: calculateTotalNights(
//                     new_checkin_date,
//                     new_checkout_date
//                   ),
//                 };
//               }
//               return booking;
//             }),
//             primaryBooking: {
//               ...parsedBookingDetails.primaryBooking,
//               checkin_date: new_checkin_date,
//               checkout_date: new_checkout_date,
//               length_of_stay: calculateTotalNights(
//                 new_checkin_date,
//                 new_checkout_date
//               ),
//             },
//           };

//           localStorage.setItem(
//             "bookingDetails",
//             JSON.stringify(updatedBookingDetails)
//           );
//         }

//         setTimeout(() => {
//           navigate("/reservation/options/booking/welcome");
//         }, 3000);
//         setShowPopup(false);
//       } else {
//         console.error("Failed to modify reservation:", data);
//         const errorMessage =
//           data.message || data.detail || "Failed to modify the reservation.";
//         setPopupMessage(errorMessage);
//         setShowErrorPopup(true);
//       }
//     } catch (error) {
//       console.error("Error occurred while modifying reservation:", error);
//       setPopupMessage("An error occurred while modifying reservation.");
//       setShowErrorPopup(true);
//     }
//   };

//   const fetchPreviousReservationDetails = async () => {
//     setLoadingPreviousReservation(true);
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/modify/${bookingId}/options`,
//         {
//           method: "GET",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       const data = await response.json();
//       if (response.ok && data.success) {
//         const transformedData = {
//           booking: {
//             order_id: orderId,
//             room_type: data.data.current_room_type,
//             checkin_date: data.data.booking_actual_dates.checkin,
//             checkout_date: data.data.booking_actual_dates.checkout,
//             number_of_guests: numberOfGuests,
//             length_of_stay: calculateTotalNights(
//               data.data.booking_actual_dates.checkin,
//               data.data.booking_actual_dates.checkout
//             ),
//           },
//           payment_summary: {
//             total_amount: data.data.current_room_total,
//           },
//         };

//         setPreviousReservationDetails(transformedData);
//         setShowPreviousReservationPopup(true);
//       } else {
//         console.error("Failed to fetch reservation details:", data);
//         setPopupMessage(
//           data.message || data.detail || "Failed to fetch reservation details."
//         );
//         setShowErrorPopup(true);
//       }
//     } catch (error) {
//       console.error(
//         "Error occurred while fetching reservation details:",
//         error
//       );
//       setPopupMessage("An error occurred while fetching reservation details.");
//       setShowErrorPopup(true);
//     } finally {
//       setLoadingPreviousReservation(false);
//     }
//   };

//   const closePopup = () => {
//     setShowPopup(false);
//   };

//   const closeErrorPopup = () => {
//     setShowErrorPopup(false);
//   };

//   const closePreviousReservationPopup = () => {
//     setShowPreviousReservationPopup(false);
//   };

//   // Helper function to format currency
//   const formatCurrency = (amount) => {
//     const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

//     if (isNaN(numAmount)) return "₹0.00";

//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(numAmount);
//   };

//   // Calculate extended days
//   const calculateExtendedDays = () => {
//     if (!previewData?.room_preview) return 0;
//     return (
//       previewData.room_preview.total_nights -
//       (previewData.room_preview.breakdown_old?.length || 0)
//     );
//   };

//   const extendedDays = calculateExtendedDays();

//   // Filter out duplicate tax entries from price details
//   const getFilteredPriceDetails = () => {
//     if (!previewData?.room_preview?.price_details) return {};

//     const priceDetails = { ...previewData.room_preview.price_details };

//     // Remove the duplicate fixed_tax_amount if it exists
//     if ('fixed_tax_amount' in priceDetails) {
//       delete priceDetails.fixed_tax_amount;
//     }

//     return priceDetails;
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col items-center justify-start py-8 px-4 relative">
//       {/* Back Navigation */}
//       <div className="w-full max-w-7xl mb-3">
//         <Link
//           to="/reservation/options/booking/welcome"
//           className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-all duration-200 font-medium group"
//         >
//           <div className=" group-hover:border-gray-300 group-hover:shadow-sm transition-all">
//             <IoMdArrowRoundBack size={18} />
//           </div>
//           <span className="group-hover:translate-x-[-2px] transition-transform">
//             Back to Dashboard
//           </span>
//         </Link>
//       </div>

//       {/* Main Container */}
//       <div className="w-full max-w-7xl">
//         {/* Header */}
//         <div className="text-center mb-6">
//           <div className="inline-flex items-center gap-3 "></div>
//           <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
//             Reschedule Your Stay
//           </h1>
//           <p className="text-gray-600 max-w-2xl mx-auto text-lg">
//             Adjust your reservation dates to better suit your travel plans. All
//             changes are subject to availability.
//           </p>
//         </div>

//         {/* Main Content Grid */}
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Left Column - Combined Card */}
//           <div className="lg:col-span-2">
//             {/* Combined Reservation & Date Selection Card */}
//             <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
//               {/* Card Header */}
//               <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
//                 <div className="flex items-center gap-4">
//                   <div className="p-3 rounded-xl bg-gray-300 border border-gray-200">
//                     <FaCalendarAlt className="text-black text-xl" />
//                   </div>
//                   <div>
//                     <h2 className="text-xl font-bold text-gray-900">
//                       Reschedule Your Booking
//                     </h2>
//                     <p className="text-gray-600 text-sm">
//                       Update your reservation details and dates
//                     </p>
//                   </div>
//                 </div>
//               </div>

//               {/* Reservation Details Section */}
//               <div className="p-7 border-b border-gray-100">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-6">
//                   Reservation Details
//                 </h3>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                   <div className="space-y-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-2">
//                         Guest Name
//                       </label>
//                       <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
//                         <p className="text-gray-900">{guestName}</p>
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-2">
//                         Reservation Number
//                       </label>
//                       <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
//                         <p className="text-gray-900 font-mono">{orderId}</p>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="space-y-4">
//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-2">
//                         Contact Number
//                       </label>
//                       <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
//                         <p className="text-gray-900">{guestPhone}</p>
//                       </div>
//                     </div>

//                     <div>
//                       <label className="block text-sm font-medium text-gray-700 mb-2">
//                         Number of Guests
//                       </label>
//                       <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
//                         <p className="text-gray-900">
//                           {numberOfGuests}{" "}
//                           {numberOfGuests === 1 ? "Guest" : "Guests"}
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Date Selection Section */}
//               <div className="p-7">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-3">
//                   Select New Dates
//                 </h3>

//                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
//                   {/* Check-in Date */}
//                   <div>
//                     <div className="mb-2">
//                       <label className="block text-sm font-medium text-gray-700">
//                         Check-in Date
//                       </label>
//                     </div>
//                     <div className="relative">
//                       <input
//                         type="date"
//                         disabled
//                         value={formatDateForInput(startDate)}
//                         onChange={handleStartDateChange}
//                         min={formatDateForInput(new Date(originalCheckin))}
//                         max={formatDateForInput(maxDate)}
//                         className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
//                       />
//                     </div>
//                   </div>

//                   {/* Check-out Date */}
//                   <div>
//                     <div className="mb-2">
//                       <label className="block text-sm font-medium text-gray-700">
//                         Check-out Date
//                       </label>
//                     </div>
//                     <input
//                       type="date"
//                       value={formatDateForInput(endDate)}
//                       onChange={handleEndDateChange}
//                       min={formatDateForInput(startDate || minDate)}
//                       max={formatDateForInput(maxDate)}
//                       className="w-full p-3 border border-gray-300 rounded-lg hover:border-gray-400 focus:border-gray-600 focus:ring-1 focus:ring-gray-600"
//                     />
//                   </div>
//                 </div>

//                 {/* Validation Message */}
//                 {showValidationMessage && validationError && (
//                   <div className="mb-6">
//                     <div className="p-4 bg-red-50 rounded-lg border border-red-200">
//                       <div className="flex items-start gap-3">
//                         <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
//                         <div>
//                           <p className="font-medium text-red-700 mb-1">
//                             Please adjust your dates
//                           </p>
//                           <p className="text-red-600 text-sm">
//                             {validationError}
//                           </p>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 )}

//                 {/* Stay Duration Comparison */}
//                 <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-8">
//                   <div className="flex justify-between items-center">
//                     <div>
//                       <p className="text-sm text-gray-600">Current Stay</p>
//                       <p className="text-lg font-semibold text-gray-800">
//                         {calculateTotalNights(
//                           originalCheckin,
//                           originalCheckout
//                         )}{" "}
//                         nights
//                       </p>
//                     </div>
//                     <div className="text-right">
//                       <p className="text-sm text-gray-600">New Stay</p>
//                       <p className="text-lg font-semibold text-gray-800">
//                         {calculateTotalNights(
//                           formatDateForInput(startDate),
//                           formatDateForInput(endDate)
//                         )}{" "}
//                         nights
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Action Button */}
//                 <div>
//                   <button
//                     onClick={handleUpdateDate}
//                     disabled={loadingPreview}
//                     className="w-full py-3.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
//                   >
//                     {loadingPreview ? (
//                       <>
//                         <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
//                         Calculating Changes...
//                       </>
//                     ) : (
//                       "Preview Changes & Pricing"
//                     )}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Right Column - Policy & Information */}
//           <div className="space-y-8">
//             {/* Policy Information */}
//             <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
//               <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
//                 <div className="flex items-center gap-3">
//                   <div className="p-2 rounded-xl bg-gray-300 border border-gray-200">
//                     <FaInfoCircle className="text-black" />
//                   </div>
//                   <h3 className="font-bold text-gray-900">
//                     Rescheduling Policy
//                   </h3>
//                 </div>
//               </div>
//               <div className="p-6">
//                 <ul className="space-y-4">
//                   <li className="flex items-start gap-3">
//                     <div className="p-1 rounded-full bg-gray-300 mt-0.5">
//                       <FaCalendarCheck className="text-black text-sm" />
//                     </div>
//                     <span className="text-gray-700">
//                       Extension requests subject to availability and may incur
//                       additional charges
//                     </span>
//                   </li>
//                   <li className="flex items-start gap-3">
//                     <div className="p-1 rounded-full bg-gray-300 mt-0.5">
//                       <FaExclamationTriangle className="text-black text-sm" />
//                     </div>
//                     <span className="text-gray-700">
//                       Early checkout may be subject to fees per hotel policy
//                     </span>
//                   </li>
//                   <li className="flex items-start gap-3">
//                     <div className="p-1 rounded-full bg-gray-300 mt-0.5">
//                       <FaShieldAlt className="text-black text-sm" />
//                     </div>
//                     <span className="text-gray-700">
//                       Room rates are subject to change based on availability
//                     </span>
//                   </li>
//                   <li className="flex items-start gap-3">
//                     <div className="p-1 rounded-full bg-gray-300 mt-0.5">
//                       <FaClock className="text-black text-sm" />
//                     </div>
//                     <span className="text-gray-700">
//                       Changes must be made at least 24 hours before original
//                       check-in
//                     </span>
//                   </li>
//                 </ul>
//               </div>
//             </div>

//             {/* Help Card - WITH HOVER TOOLTIPS */}
//             <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg border border-blue-200 p-6">
//               <div className="flex items-center gap-3 mb-4">
//                 <div className="p-3 rounded-xl bg-blue-100">
//                   <FaQuestionCircle className="text-blue-600 text-xl" />
//                 </div>
//                 <div>
//                   <h3 className="font-bold text-gray-900">Need Help?</h3>
//                   <p className="text-gray-600 text-sm">
//                     Our team is here to assist you
//                   </p>
//                 </div>
//               </div>
//               <div className="space-y-3">
//                 <div className="relative group">
//                   <a
//                     href={`mailto:${hotelEmail}`}
//                     className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group-hover:bg-blue-50"
//                   >
//                     <FaEnvelope className="text-gray-500 group-hover:text-blue-600 transition-colors" />
//                     <span className="text-gray-700 group-hover:text-gray-900">
//                       Email Support
//                     </span>
//                   </a>
//                   {/* Tooltip for email */}
//                   <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
//                     <span className="font-medium">{hotelEmail}</span>
//                     <div className="absolute top-full left-6 -mt-1 border-4 border-transparent border-t-gray-900"></div>
//                   </div>
//                 </div>

//                 <div className="relative group">
//                   <a
//                     href={`tel:${phone}`}
//                     className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group-hover:bg-blue-50"
//                   >
//                     <FaPhoneAlt className="text-gray-500 group-hover:text-blue-600 transition-colors" />
//                     <span className="text-gray-700 group-hover:text-gray-900">
//                       Call Reception
//                     </span>
//                   </a>
//                   {/* Tooltip for phone */}
//                   <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
//                     <span className="font-medium">{phone}</span>
//                     <div className="absolute top-full left-6 -mt-1 border-4 border-transparent border-t-gray-900"></div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Preview Popup - Enhanced */}
//       {showPopup && previewData && (
//         <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
//           <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-300 animate-slideUp">
//             {/* Header */}
//             <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-gray-800 text-white p-7 rounded-t-2xl">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-4">
//                   <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
//                     <FaReceipt className="text-white text-2xl" />
//                   </div>
//                   <div>
//                     <h2 className="text-2xl font-bold">Reschedule Preview</h2>
//                     <p className="text-gray-300">
//                       Review your changes before confirming
//                     </p>
//                   </div>
//                 </div>
//                 <button
//                   onClick={closePopup}
//                   className="p-2 rounded-lg hover:bg-white/10 transition-colors"
//                 >
//                   <svg
//                     className="w-6 h-6"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M6 18L18 6M6 6l12 12"
//                     />
//                   </svg>
//                 </button>
//               </div>
//             </div>

//             {/* Content */}
//             <div className="p-7 space-y-7">
//               {/* Stay Comparison */}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200">
//                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
//                     <FaCalendarAlt className="text-gray-600" />
//                     Original Stay
//                   </h3>
//                   <div className="space-y-3">
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Dates</p>
//                       <p className="text-gray-900 font-medium">
//                         {formatDateToDisplay(originalCheckin)} -{" "}
//                         {formatDateToDisplay(originalCheckout)}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Duration</p>
//                       <p className="text-gray-900 font-bold text-xl">
//                         {previewData.room_preview.breakdown_old?.length || 0}{" "}
//                         Nights
//                       </p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Total</p>
//                       <p className="text-gray-900 font-bold text-xl">
//                         {formatCurrency(previewData.room_preview.old_total)}
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200 relative">
//                   <div className="absolute -top-3 -right-3 px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-bold rounded-full">
//                     NEW
//                   </div>
//                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
//                     <FaCalendarAlt className="text-blue-600" />
//                     New Stay
//                   </h3>
//                   <div className="space-y-3">
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Dates</p>
//                       <p className="text-gray-900 font-medium">
//                         {formatDateToDisplay(
//                           previewData.room_preview.new_checkin
//                         )}{" "}
//                         -{" "}
//                         {formatDateToDisplay(
//                           previewData.room_preview.new_checkout
//                         )}
//                       </p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Duration</p>
//                       <p className="text-gray-900 font-bold text-xl">
//                         {previewData.room_preview.total_nights} Nights
//                       </p>
//                     </div>
//                     <div>
//                       <p className="text-sm text-gray-600 mb-1">Total</p>
//                       <p className="text-gray-900 font-bold text-xl">
//                         {formatCurrency(previewData.room_preview.new_total)}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Price Difference Highlight */}
//               <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-6 text-white">
//                 <div className="flex flex-col md:flex-row justify-between items-center gap-4">
//                   <div>
//                     <p className="text-gray-300">Price Difference</p>
//                     <p className="text-3xl font-bold mt-1">
//                       {formatCurrency(
//                         previewData.room_preview.price_difference
//                       )}
//                     </p>
//                     <p className="text-sm text-gray-400 mt-2">
//                       Including all taxes & fees
//                     </p>
//                   </div>
//                   <div className="text-center md:text-right">
//                     <p className="text-gray-300">Change Type</p>
//                     <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mt-1">
//                       {previewData.room_preview.modification_type}
//                     </span>
//                     {extendedDays > 0 && (
//                       <p className="text-green-300 text-sm mt-2">
//                         +{extendedDays} additional nights
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               </div>

//               {/* Detailed Breakdown - FIXED: Removed duplicate tax amount */}
//               <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200">
//                 <div className="flex justify-between items-center mb-4">
//                   <h3 className="font-bold text-gray-800">
//                     Detailed Price Breakdown
//                   </h3>
//                   <button
//                     onClick={() => setShowBreakdown(!showBreakdown)}
//                     className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
//                   >
//                     {showBreakdown ? "Hide Details" : "Show Details"}
//                   </button>
//                 </div>

//                 {showBreakdown && previewData.room_preview.price_details && (
//                   <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-200">
//                     {Object.entries(getFilteredPriceDetails()).map(
//                       ([key, value]) => (
//                         <div
//                           key={key}
//                           className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
//                         >
//                           <span className="text-gray-600 capitalize">
//                             {key.replace(/_/g, " ")}
//                           </span>
//                           <span className="font-medium text-gray-900">
//                             {formatCurrency(value)}
//                           </span>
//                         </div>
//                       )
//                     )}
//                   </div>
//                 )}
//               </div>

//               {/* Final Summary */}
//               <div className="bg-gradient-to-r from-green-50 to-white rounded-xl p-5 border border-green-200">
//                 <h3 className="font-bold text-gray-800 mb-4">
//                   Payment Summary
//                 </h3>
//                 <div className="space-y-3">
//                   <div className="flex justify-between items-center">
//                     <span className="text-gray-700">Current Balance Due</span>
//                     <span className="font-medium text-gray-900">
//                       {formatCurrency(
//                         previewData.combined.current_due_before_preview
//                       )}
//                     </span>
//                   </div>
//                   <div className="flex justify-between items-center">
//                     <span className="text-gray-700">Additional Charges</span>
//                     <span className="font-medium text-green-600">
//                       +{" "}
//                       {formatCurrency(
//                         previewData.combined.net_financial_impact
//                       )}
//                     </span>
//                   </div>
//                   <div className="border-t border-gray-300 pt-3">
//                     <div className="flex justify-between items-center">
//                       <span className="text-lg font-bold text-gray-900">
//                         New Total Balance
//                       </span>
//                       <span className="text-2xl font-bold text-gray-900">
//                         {formatCurrency(
//                           previewData.combined.fixed_current_total
//                         )}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Footer Actions */}
//             <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
//               <div className="flex flex-col sm:flex-row justify-end gap-3">
//                 <button
//                   onClick={closePopup}
//                   className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleContinue}
//                   className="px-8 py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-xl hover:from-gray-800 hover:to-gray-700 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
//                 >
//                   Confirm Changes
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Error Popup - Enhanced WITH HOVER TOOLTIPS */}
//       {showErrorPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
//           <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-300 overflow-hidden animate-scaleIn">
//             <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>

//             <div className="p-8 text-center">
//               <div className="mb-6">
//                 <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-red-100 to-orange-100">
//                   <BsFillExclamationTriangleFill className="text-red-600 text-4xl" />
//                 </div>
//               </div>

//               <h2 className="text-2xl font-bold text-gray-900 mb-3">
//                 Unable to Proceed
//               </h2>
//               <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
//                 <p className="text-red-700 font-medium whitespace-pre-line">
//                   {popupMessage}
//                 </p>
//               </div>

//               <div className="bg-gray-50 rounded-xl p-4 mb-6">
//                 <p className="text-gray-700 mb-2">Need assistance?</p>
//                 <div className="flex flex-col gap-2">
//                   <div className="relative group">
//                     <a
//                       href={`mailto:${hotelEmail}`}
//                       className="text-gray-900 hover:text-gray-700 font-medium inline-flex items-center gap-2 justify-center w-full"
//                     >
//                       <FaEnvelope />
//                       Email Support
//                     </a>
//                     <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
//                       {hotelEmail}
//                       <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
//                     </div>
//                   </div>

//                   <div className="relative group">
//                     <a
//                       href={`tel:${phone}`}
//                       className="text-gray-900 hover:text-gray-700 font-medium inline-flex items-center gap-2 justify-center w-full"
//                     >
//                       <FaPhoneAlt />
//                       Call {phone}
//                     </a>
//                     <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap shadow-lg z-50">
//                       {phone}
//                       <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <button
//                 onClick={closeErrorPopup}
//                 className="w-full py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-xl hover:from-gray-800 hover:to-gray-700 transition-all"
//               >
//                 Try Again
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer
//         position="top-center"
//         autoClose={4000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// };

// export default Identity;
